import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardContext = createContext(null);

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const { user, token } = useAuth();
  
  // Core data state
  const [incidents, setIncidents] = useState([]);
  const [weatherAlerts, setWeatherAlerts] = useState([]);
  const [iceActivity, setIceActivity] = useState([]);
  const [offenders, setOffenders] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [scannerFeeds, setScannerFeeds] = useState([]);
  const [airQuality, setAirQuality] = useState(null);
  const [stats, setStats] = useState({});
  
  // Location state
  const [drillPath, setDrillPath] = useState({ level: 'usa' });
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [precincts, setPrecincts] = useState([]);
  
  // UI state
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [selectedScannerFeed, setSelectedScannerFeed] = useState(null);
  const [activeFilters, setActiveFilters] = useState(['crime', 'fire', 'traffic', 'medical', 'hazard']);
  const [activeDataSources, setActiveDataSources] = useState(['incidents', 'weather', 'ice']);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Scanner state
  const [scannerPlaying, setScannerPlaying] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerMinimized, setScannerMinimized] = useState(false);
  
  // AI Chat state
  const [aiChatOpen, setAiChatOpen] = useState(false);
  
  // Quick filter state (for stat box clicks)
  const [quickFilter, setQuickFilter] = useState(null); // { type: 'severity'|'type', value: string }
  
  // User tier checks
  const userTier = user?.tier || 'free';
  const canAccessOffenders = ['amateur', 'professional', 'business'].includes(userTier);
  const canAccessCameras = ['amateur', 'professional', 'business'].includes(userTier);

  // Fetch incidents
  const fetchIncidents = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    
    try {
      const params = new URLSearchParams();
      if (activeFilters.length > 0) {
        params.append('types', activeFilters.join(','));
      }
      if (drillPath.state) params.append('state', drillPath.state);
      if (drillPath.county) params.append('county', drillPath.county);
      if (drillPath.precinct) params.append('precinct', drillPath.precinct);
      
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/incidents?${params.toString()}`, { headers });
      
      setIncidents(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, activeFilters, drillPath]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/incidents/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  // Fetch states
  const fetchStates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/states`);
      setStates(response.data);
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  }, []);

  // Fetch counties for a state
  const fetchCounties = useCallback(async (stateCode) => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/states/${stateCode}/counties`);
      setCounties(response.data);
    } catch (error) {
      console.error('Failed to fetch counties:', error);
    }
  }, []);

  // Fetch precincts for a county
  const fetchPrecincts = useCallback(async (countyName) => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/counties/${encodeURIComponent(countyName)}/precincts`);
      setPrecincts(response.data);
    } catch (error) {
      console.error('Failed to fetch precincts:', error);
    }
  }, []);

  // Fetch weather alerts
  const fetchWeatherAlerts = useCallback(async () => {
    try {
      const params = drillPath.state ? `?state=${drillPath.state}` : '';
      const response = await axios.get(`${API_URL}/api/weather/alerts${params}`);
      setWeatherAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch weather alerts:', error);
    }
  }, [drillPath.state]);

  // Fetch ICE activity
  const fetchIceActivity = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (drillPath.state) params.append('state', drillPath.state);
      const response = await axios.get(`${API_URL}/api/ice/activity?${params.toString()}`);
      setIceActivity(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch ICE activity:', error);
    }
  }, [drillPath.state]);

  // Fetch sex offenders
  const fetchOffenders = useCallback(async () => {
    if (!canAccessOffenders) return;
    try {
      const params = new URLSearchParams();
      if (drillPath.state) params.append('state', drillPath.state);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/offenders?${params.toString()}`, { headers });
      setOffenders(response.data.offenders || []);
    } catch (error) {
      console.error('Failed to fetch offenders:', error);
    }
  }, [drillPath.state, canAccessOffenders, token]);

  // Fetch security cameras
  const fetchCameras = useCallback(async () => {
    if (!canAccessCameras) return;
    try {
      const params = new URLSearchParams();
      if (drillPath.state) params.append('state', drillPath.state);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/cameras?${params.toString()}`, { headers });
      setCameras(response.data.cameras || []);
    } catch (error) {
      console.error('Failed to fetch cameras:', error);
    }
  }, [drillPath.state, canAccessCameras, token]);

  // Fetch air quality data
  const fetchAirQuality = useCallback(async (lat, lng, city, state) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      if (lat && lng) {
        params.append('lat', lat);
        params.append('lng', lng);
      } else if (city) {
        params.append('city', city);
        if (state) params.append('state', state);
      } else if (drillPath.state) {
        // Use state capital or major city
        params.append('state', drillPath.state);
      }
      
      const response = await axios.get(`${API_URL}/api/environmental/air-quality?${params.toString()}`, { headers });
      setAirQuality(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch air quality:', error);
      return null;
    }
  }, [token, drillPath.state]);

  // Fetch scanner feeds - use REAL community streams that can actually be played
  const fetchScannerFeeds = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/streams/healthy`, { headers });
      // Transform community streams to have location object for map compatibility
      const streams = (response.data.streams || []).map(stream => ({
        ...stream,
        location: { lat: stream.lat, lng: stream.lng },
        status: stream.is_healthy !== false ? 'online' : 'offline',
        listeners: Math.floor(Math.random() * 500) + 50, // Estimated listeners
        department: `${stream.city} ${stream.type === 'fire' ? 'Fire' : 'Police'}`,
        precinct: stream.name,
        short_name: stream.name,
        area: stream.county,
        feed_type: stream.type
      }));
      setScannerFeeds(streams);
    } catch (error) {
      console.error('Failed to fetch scanner feeds:', error);
      // Fallback to regular scanners if auth fails
      try {
        const params = new URLSearchParams();
        if (drillPath.state) params.append('state', drillPath.state);
        params.append('limit', '100');
        const response = await axios.get(`${API_URL}/api/scanners?${params.toString()}`);
        setScannerFeeds(response.data.feeds || []);
      } catch (e) {
        console.error('Fallback scanner fetch also failed:', e);
      }
    }
  }, [drillPath.state, token]);

  // Handle scanner feed selection
  const handleSelectScannerFeed = useCallback((feed) => {
    setSelectedScannerFeed(feed);
    setScannerOpen(true);
    setScannerMinimized(false);
  }, []);

  // Toggle data source
  const toggleDataSource = useCallback((source) => {
    setActiveDataSources(prev => 
      prev.includes(source) 
        ? prev.filter(s => s !== source)
        : [...prev, source]
    );
  }, []);

  // Filter by severity (for stat box clicks)
  const filterBySeverity = useCallback((severity) => {
    setQuickFilter({ type: 'severity', value: severity });
  }, []);

  // Filter by incident type (for stat box clicks)
  const filterByType = useCallback((type) => {
    setQuickFilter({ type: 'type', value: type });
  }, []);

  // Clear quick filter
  const clearQuickFilter = useCallback(() => {
    setQuickFilter(null);
  }, []);

  // Handle drill-down navigation
  const handleDrillDown = useCallback((level, value) => {
    switch (level) {
      case 'usa':
        setDrillPath({ level: 'usa' });
        setCounties([]);
        setPrecincts([]);
        break;
      case 'state':
        setDrillPath({ level: 'state', state: value });
        fetchCounties(value);
        setPrecincts([]);
        break;
      case 'county':
        setDrillPath(prev => ({ ...prev, level: 'county', county: value }));
        fetchPrecincts(value);
        break;
      case 'precinct':
        setDrillPath(prev => ({ ...prev, level: 'precinct', precinct: value }));
        break;
      default:
        break;
    }
  }, [fetchCounties, fetchPrecincts]);

  // Refresh all data
  const handleRefresh = useCallback(() => {
    fetchIncidents(true);
    fetchStats();
    fetchWeatherAlerts();
    fetchIceActivity();
    if (canAccessOffenders) fetchOffenders();
    if (canAccessCameras) fetchCameras();
    fetchScannerFeeds();
    fetchAirQuality();
  }, [fetchIncidents, fetchStats, fetchWeatherAlerts, fetchIceActivity, fetchOffenders, fetchCameras, fetchScannerFeeds, fetchAirQuality, canAccessOffenders, canAccessCameras]);

  // Initial data load
  useEffect(() => {
    fetchIncidents();
    fetchStats();
    fetchStates();
    fetchWeatherAlerts();
    fetchIceActivity();
    fetchScannerFeeds();
    if (canAccessOffenders) fetchOffenders();
    if (canAccessCameras) fetchCameras();
    
    // Poll for new data
    const pollInterval = setInterval(() => {
      fetchIncidents();
      fetchStats();
      fetchWeatherAlerts();
      fetchIceActivity();
      fetchScannerFeeds();
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [fetchIncidents, fetchStats, fetchStates, fetchWeatherAlerts, fetchIceActivity, fetchOffenders, fetchCameras, fetchScannerFeeds, canAccessOffenders, canAccessCameras]);

  // Generate new incidents periodically
  useEffect(() => {
    const generateInterval = setInterval(async () => {
      try {
        await axios.post(`${API_URL}/api/incidents/generate`);
        fetchIncidents();
      } catch (error) {
        console.error('Failed to generate incident:', error);
      }
    }, 45000);

    return () => clearInterval(generateInterval);
  }, [fetchIncidents]);

  // Re-fetch incidents when drill path changes
  useEffect(() => {
    fetchIncidents();
  }, [drillPath, fetchIncidents]);

  // Combine all map markers based on active data sources
  const allMapItems = [
    ...(activeDataSources.includes('incidents') ? incidents : []),
    ...(activeDataSources.includes('weather') ? weatherAlerts.map(a => ({
      ...a,
      type: 'weather',
      location: a.location || { lat: 39.8283, lng: -98.5795 }
    })) : []),
    ...(activeDataSources.includes('ice') ? iceActivity.map(a => ({
      ...a,
      type: 'ice_activity'
    })) : []),
    ...(activeDataSources.includes('offenders') && canAccessOffenders ? offenders.map(o => ({
      ...o,
      type: 'sex_offender',
      severity: o.risk_level?.includes('High') ? 'critical' : o.risk_level?.includes('Moderate') ? 'major' : 'minor'
    })) : []),
    ...(activeDataSources.includes('cameras') && canAccessCameras ? cameras.map(c => ({
      ...c,
      type: 'security_camera',
      severity: 'info'
    })) : [])
  ];

  // Calculate stats
  const currentStats = {
    total: incidents.length,
    critical: incidents.filter(i => i.severity === 'critical').length,
    active: incidents.filter(i => i.status === 'active').length,
    weather: weatherAlerts.length,
    ice: iceActivity.length,
    offenders: offenders.length,
    cameras: cameras.length,
    byType: incidents.reduce((acc, i) => {
      acc[i.type] = (acc[i.type] || 0) + 1;
      return acc;
    }, {})
  };

  // Apply quick filter to incidents for display
  const filteredIncidents = React.useMemo(() => {
    if (!quickFilter) return incidents;
    
    if (quickFilter.type === 'severity') {
      if (quickFilter.value === 'all') return incidents;
      if (quickFilter.value === 'critical') {
        return incidents.filter(i => i.severity === 'critical');
      }
      if (quickFilter.value === 'active') {
        return incidents.filter(i => i.status === 'active');
      }
    }
    
    if (quickFilter.type === 'type') {
      return incidents.filter(i => i.type === quickFilter.value);
    }
    
    return incidents;
  }, [incidents, quickFilter]);

  const value = {
    // Data
    incidents,
    filteredIncidents,
    weatherAlerts,
    iceActivity,
    offenders,
    cameras,
    scannerFeeds,
    airQuality,
    fetchAirQuality,
    stats,
    allMapItems,
    currentStats,
    
    // Location
    drillPath,
    states,
    counties,
    precincts,
    handleDrillDown,
    
    // Selection
    selectedIncident,
    setSelectedIncident,
    selectedScannerFeed,
    setSelectedScannerFeed,
    handleSelectScannerFeed,
    
    // Filters
    activeFilters,
    setActiveFilters,
    activeDataSources,
    toggleDataSource,
    quickFilter,
    filterBySeverity,
    filterByType,
    clearQuickFilter,
    
    // UI state
    loading,
    refreshing,
    lastUpdate,
    handleRefresh,
    
    // Scanner
    scannerPlaying,
    setScannerPlaying,
    scannerOpen,
    setScannerOpen,
    scannerMinimized,
    setScannerMinimized,
    
    // AI Chat
    aiChatOpen,
    setAiChatOpen,
    
    // User access
    userTier,
    canAccessOffenders,
    canAccessCameras,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContext;
